import AsyncStorage from "@react-native-async-storage/async-storage";

export type NativeHomeSection = {
  id: string;
  type: string;
  title: string;
  enabled: boolean;
};

export type NativeHomeConfig = {
  version: number;
  showcase: boolean;
  dynamicSectionLimit: number;
  sections: NativeHomeSection[];
  libraryToggles: Array<{ libraryId: string; libraryName: string; collectionType?: string; enabled: boolean }>;
  userViewsSourceKey?: string;
};

const STORAGE_KEY = "jf_home_config";

export const defaultHomeConfig: NativeHomeConfig = {
  version: 4,
  showcase: true,
  dynamicSectionLimit: 12,
  libraryToggles: [
    { libraryId: "movies", libraryName: "Movies", enabled: true },
    { libraryId: "shows", libraryName: "Shows", enabled: true },
    { libraryId: "music", libraryName: "Music", enabled: true },
    { libraryId: "playlists", libraryName: "Playlists", enabled: true },
  ],
  sections: [
    ["continueWatchingNextUp", "Continue Watching + Next Up"],
    ["items", "Top Rated Movies"],
    ["items", "Top Rated TV Shows"],
    ["recommendedMovies", "Recommended Movies"],
    ["recommendedShows", "Recommended TV Shows"],
    ["personalizedRecommendations", "Personalized Recommendations"],
    ["watchItAgain", "Watch It Again"],
    ["recentlyAdded", "Recently Added"],
    ["serverLibraries", "Your Libraries"],
    ["genres", "Genres"],
    ["streamingServices", "Streaming Services"],
  ].map(([type, title], index) => ({
    id: `native-${index + 1}`,
    type,
    title,
    enabled: true,
  })),
};

function normalize(value: unknown): NativeHomeConfig {
  if (!value || typeof value !== "object") return defaultHomeConfig;
  const candidate = value as Partial<NativeHomeConfig>;
  if (!Array.isArray(candidate.sections)) return defaultHomeConfig;
  let personalizedSeen = false;
  let sections = candidate.sections
    .filter((section): section is NativeHomeSection =>
      Boolean(section && section.id && section.type && section.title),
    )
    .map((section) => {
      const legacyRecommended = section.type === "recommended"
        || section.title === "Recommended Movies"
        || section.title === "Recommended Series"
        || section.title === "Recommended TV Shows";
      if (!legacyRecommended) return { ...section, enabled: section.enabled !== false };
      const shows = /series|shows/i.test(section.title);
      return {
        ...section,
        type: shows ? "recommendedShows" : "recommendedMovies",
        title: shows ? "Recommended TV Shows" : "Recommended Movies",
        enabled: section.enabled !== false,
      };
    })
    .filter((section) => {
      if (section.type !== "personalizedRecommendations") return true;
      if (personalizedSeen) return false;
      personalizedSeen = true;
      return true;
    });
  if (Number(candidate.version) < 3) {
    const missing = [
      ["recommendedMovies", "Recommended Movies"],
      ["recommendedShows", "Recommended TV Shows"],
    ].filter(([type]) => !sections.some((section) => section.type === type));
    const topShowsIndex = sections.findIndex((section) => section.title === "Top Rated TV Shows");
    const topMoviesIndex = sections.findIndex((section) => section.title === "Top Rated Movies");
    const personalizedIndex = sections.findIndex((section) => section.type === "personalizedRecommendations");
    const insertionIndex = topShowsIndex >= 0
      ? topShowsIndex + 1
      : topMoviesIndex >= 0
        ? topMoviesIndex + 1
        : personalizedIndex >= 0
          ? personalizedIndex
          : sections.length;
    sections = [
      ...sections.slice(0, insertionIndex),
      ...missing.map(([type, title]) => ({
        id: `native-${type}`,
        type,
        title,
        enabled: true,
      })),
      ...sections.slice(insertionIndex),
    ];
  }
  if (Number(candidate.version) < 4 && !sections.some((section) => section.type === "serverLibraries")) {
    const recentIndex = sections.findIndex((section) => section.type === "recentlyAdded");
    const insertionIndex = recentIndex >= 0 ? recentIndex + 1 : sections.length;
    sections = [
      ...sections.slice(0, insertionIndex),
      { id: "native-server-libraries", type: "serverLibraries", title: "Your Libraries", enabled: true },
      ...sections.slice(insertionIndex),
    ];
  }
  return {
    version: 4,
    showcase: candidate.showcase !== false,
    dynamicSectionLimit: Math.max(
      0,
      Math.min(
        80,
        Math.round(
          Number(candidate.version) < 2 && Number(candidate.dynamicSectionLimit) === 10
            ? 12
            : Number(candidate.dynamicSectionLimit) || 0,
        ),
      ),
    ),
    sections,
    libraryToggles: Array.isArray(candidate.libraryToggles)
      ? candidate.libraryToggles.filter((entry) => Boolean(entry?.libraryId && entry?.libraryName)).map((entry) => ({ ...entry, enabled: entry.enabled !== false }))
      : defaultHomeConfig.libraryToggles,
    userViewsSourceKey: candidate.userViewsSourceKey,
  };
}

export async function readHomeConfig() {
  try {
    const stored = await AsyncStorage.getItem(STORAGE_KEY);
    return stored ? normalize(JSON.parse(stored)) : defaultHomeConfig;
  } catch {
    return defaultHomeConfig;
  }
}

export async function saveHomeConfig(config: NativeHomeConfig) {
  const normalized = normalize(config);
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

export async function resetHomeConfig() {
  await AsyncStorage.removeItem(STORAGE_KEY);
  return defaultHomeConfig;
}
